// 화면에서 쓰는 변경 — v0.3 State.kt 의 동작을 mutation 으로. 업무 규칙(자동 경고카드·자동 완료)은 서버가 최종 판정
import { cachedMe, getToken, payloadOf, type Me } from "../auth";
import { getView, mutate, newId } from "./store";
import { St, type Item, type Session, type Settings, type Slot, type Status, type Student, type Warn } from "./types";

export function currentMe(): Me | null {
  const p = payloadOf(getToken());
  if (!p) return null;
  const row = getView().staff.find((s) => s.id === p.sid);
  if (row) return { id: row.id, name: row.name, admin: row.admin };
  return cachedMe();
}
const who = () => currentMe()?.name ?? "";

/** 개별일정 등록(+ 오늘 남은 시험지). 새 행이라 v0.3 필드를 전부 채워 보낸다 */
export function registerSession(studentId: string, date: string, time: string, newItems: { week: string; title: string }[], note = ""): Session {
  const now = Date.now();
  const s: Session = { id: newId(), studentId, date, time, status: St.PLAN, late: false, createdBy: who(), createdAt: now, closedBy: null, note };
  mutate("sessions", "upsert", s);
  for (const it of newItems) {
    const item: Item = {
      id: newId(), studentId, week: it.week, title: it.title, done: false, createdBy: who(), createdAt: now, doneAt: null, doneBy: null, doneSession: null,
    };
    mutate("items", "upsert", item);
  }
  return s;
}

export const deleteSession = (id: string) => mutate("sessions", "delete", { id });

/** 시험지 통과 체크 ↔ 취소. 바뀐 4필드만 보낸다(서버 필드 단위 병합 예외) */
export function toggleItem(item: Item, session: Session | null) {
  const by = who();
  const data = item.done
    ? { id: item.id, done: false, doneAt: null, doneBy: null, doneSession: null }
    : { id: item.id, done: true, doneAt: Date.now(), doneBy: by, doneSession: session?.id ?? null };
  mutate("items", "upsert", data, session ? { sessionId: session.id, by } : undefined);
}

/** 지각 표시 — 경고카드는 서버가 auto-late-<일정id> 로 만든다 */
export const setLate = (s: Session, late: boolean) => mutate("sessions", "upsert", { id: s.id, late });

/** 마감: 완료·미완료·노쇼·예정(되돌리기). 노쇼 경고카드는 서버가 만든다 */
export const closeSession = (s: Session, status: Status) =>
  mutate("sessions", "upsert", { id: s.id, status, closedBy: status === St.PLAN ? null : who() });

/* ───────── S04: 일정 이동·학생·시험지·경고카드·직원·설정 ───────── */

export const moveSession = (s: Session, date: string, time: string) => mutate("sessions", "upsert", { id: s.id, date, time });

/** 학생 저장 — 새 학생이면 v0.3 필드 전부, 기존이면 바뀐 필드만. 연락처는 가짜 데이터 규칙상 비워 둔다 */
export function saveStudent(prev: Student | null, f: { name: string; school: string; memo: string; slots: Slot[] }, colorSeed: number): string {
  if (prev) {
    mutate("students", "upsert", { id: prev.id, name: f.name, school: f.school, memo: f.memo, slots: f.slots });
    return prev.id;
  }
  const COLORS = [0xff4c6ef5, 0xff12b886, 0xfff76707, 0xffe64980, 0xff7950f2, 0xff15aabf, 0xfffab005, 0xff82c91e, 0xfffa5252, 0xff228be6];
  const id = newId();
  mutate("students", "upsert", { id, name: f.name, school: f.school, phone: "", memo: f.memo, color: COLORS[colorSeed % COLORS.length], slots: f.slots });
  return id;
}
/** 학생 삭제 — 항목·일정·카드는 서버가 함께 소프트 삭제한다 */
export const deleteStudent = (id: string) => mutate("students", "delete", { id });

export function addItems(studentId: string, list: { week: string; title: string }[]) {
  const now = Date.now();
  for (const it of list) {
    mutate("items", "upsert", { id: newId(), studentId, week: it.week, title: it.title, done: false, createdBy: who(), createdAt: now, doneAt: null, doneBy: null, doneSession: null });
  }
}
export const deleteItem = (id: string) => mutate("items", "delete", { id });
/** 삭제 되돌리기 — 서버는 지운 행을 되살리지 않으므로(conflict: deleted) 같은 내용을 새 id 로 다시 만든다 */
export function recreate(entity: "items" | "warns", row: Item | Warn) {
  const { version: _v, updatedAt: _u, updatedBy: _b, deleted: _d, ...rest } = row;
  void _v; void _u; void _b; void _d;
  mutate(entity, "upsert", { ...rest, id: newId() });
}

export function addWarn(studentId: string, type: string, date: string, note: string) {
  mutate("warns", "upsert", { id: newId(), studentId, type, date, note, createdBy: who(), createdAt: Date.now(), sessionId: null });
}
export const deleteWarn = (id: string) => mutate("warns", "delete", { id });

/** 직원 저장(관리자만 — 서버도 403). PIN 은 서버에서 해시로만 저장되고 다시 내려오지 않는다 */
export function saveStaff(id: string | null, name: string, admin: boolean, pin: string | null) {
  const data: Record<string, unknown> = { id: id ?? newId(), name, admin };
  if (pin) data.pin = pin;
  mutate("staff", "upsert", data as { id: string });
}
export const deleteStaff = (id: string) => mutate("staff", "delete", { id });

export const saveSettings = (patch: Partial<Settings>) => mutate("settings", "upsert", { id: "main", ...patch });
